import RtaAnalysisPanel from './productionForecast/RtaAnalysisPanel';
import React, {
  useEffect,
  useState,
} from 'react';
import { useProjectStore } from '../hooks/useProjectStore';
import {
  useProductionForecastStore,
} from '../hooks/useProductionForecastStore';
import { ETH } from './economics/ecoShared';

import type { ForecastTab } from './productionForecast/types';

import DataPreparationPanel from './productionForecast/DataPreparationPanel';
import DeliverabilityPanel from './productionForecast/DeliverabilityPanel';
import DeclineForecastPanel from './productionForecast/DeclineForecastPanel';
import RtaPanel from './productionForecast/RtaPanel';
import UncertaintyPanel from './productionForecast/UncertaintyPanel';
import ForecastSummary from './productionForecast/ForecastSummary';
import DeclinePredictionPanel from './productionForecast/DeclinePredictionPanel';

const TABS: {
  key: ForecastTab;
  label: string;
  desc: string;
}[] = [
  {
    key: 'data',
    label: '数据准备',
    desc: '储层、井筒、压裂及生产数据',
  },
  {
    key: 'deliverability',
    label: '产能试井',
    desc: '二项式、指数式及无阻流量',
  },
  {
    key: 'decline',
    label: '递减预测',
    desc: '生产递减与 EUR 预测',
  },
  {
    key: 'rta',
    label: 'RTA 分析',
    desc: '生产动态与流动阶段分析',
  },
  {
    key: 'uncertainty',
    label: '不确定性',
    desc: '低值、基准值和高值情景',
  },
  {
    key: 'summary',
    label: '综合评价',
    desc: '产能与预测结果汇总',
  },
];

export default function ProductionForecastPage() {
  const { currentProject } =
    useProjectStore();

  const ensureProject =
    useProductionForecastStore(
      (state) =>
        state.ensureProject,
    );

  const [activeTab, setActiveTab] =
    useState<ForecastTab>(
      'data',
    );

  useEffect(() => {
    if (!currentProject) {
      return;
    }

    ensureProject(
      currentProject.id,
    );
  }, [
    currentProject,
    ensureProject,
  ]);

  if (!currentProject) {
    return (
      <div
        style={{
          padding: 40,
          color: ETH.textSec,
        }}
      >
        请先选择项目
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: ETH.bg,
        color: ETH.text,
        fontFamily: ETH.fontUI,
      }}
    >
      {/* 页面标题 */}
      <div
        style={{
          padding: '14px 20px',
          borderBottom: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 700,
          }}
        >
          产能预测
        </h2>

        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: ETH.textMuted,
          }}
        >
          基于储层、压裂、试井及生产动态数据开展产能评价与产量预测
        </div>
      </div>

      {/* 功能页签 */}
      <div
        style={{
          display: 'flex',
          padding: '0 20px',
          borderBottom: `1px solid ${ETH.headerBorder}`,
          background: ETH.canvasBg,
          overflowX: 'auto',
        }}
      >
        {TABS.map((tab) => {
          const active = activeTab === tab.key;

          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                minWidth: 135,
                padding: '12px 16px',
                border: 'none',
                borderBottom: active
                  ? '2px solid #c75b3c'
                  : '2px solid transparent',
                background: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                color: active
                  ? ETH.text
                  : ETH.textMuted,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                }}
              >
                {tab.label}
              </div>

              <div
                style={{
                  marginTop: 3,
                  fontSize: 10,
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.desc}
              </div>
            </button>
          );
        })}
      </div>

      {/* 当前功能页面 */}
      <div style={{ padding: 20 }}>
        {activeTab === 'data' && (
          <DataPreparationPanel
            projectId={
              currentProject.id
            }
          />
        )}

        {activeTab === 'deliverability' && (
          <DeliverabilityPanel
            projectId={
              currentProject.id
            }
          />
        )}

        {activeTab === 'decline' && (
          <DeclinePredictionPanel
            projectId={currentProject.id}
          />
        )}

        {activeTab === 'rta' && (
          <RtaAnalysisPanel
            projectId={currentProject.id}
          />
        )}

        {activeTab === 'uncertainty' && (
          <UncertaintyPanel projectId={currentProject.id} />
        )}

        {activeTab === 'summary' && (
          <ForecastSummary projectId={currentProject.id} />
        )}
      </div>
    </div>
  );
}